import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Archive, ArchiveRestore, Wallet, Pencil, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../api/client';

const PROFESSION_LABELS = { apotheker: 'Apotheker', dokter: 'Dokter', therapeut: 'Therapeut', kinesist: 'Kinesist', verpleegkundige: 'Verpleegkundige', anders: 'Anders' };

export default function AdminB2B() {
  const [persons, setPersons] = useState([]);
  const [filter, setFilter] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  // Always fetch with include_archived=true so the header can show an
  // accurate "Archief: N" counter regardless of the toggle state. The
  // toggle only changes what rows are rendered, not what is fetched.
  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.listB2B({ include_archived: true });
      setPersons(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const archivedCount = persons.filter(p => p.archived).length;
  const activeCount = persons.length - archivedCount;

  const filtered = persons.filter(p => {
    if (!includeArchived && p.archived) return false;
    if (filter === 'pending') return !p.isApproved;
    if (filter === 'approved') return p.isApproved;
    return true;
  });

  const archive = async (p) => {
    if (!window.confirm(`Archiveer "${p.firstName || ''} ${p.lastName || ''}"?`)) return;
    await adminApi.archivePrescriber(p._id);
    toast.success('Gearchiveerd');
    reload();
  };
  const restore = async (p) => {
    await adminApi.restorePrescriber(p._id);
    toast.success('Hersteld');
    reload();
  };

  return (
    <div data-testid="admin-b2b-page" className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-light text-foreground">B2B Aanvragen &amp; Voorschrijvers</h1>
          <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-2">
            <span data-testid="b2b-active-count">{activeCount} actief</span>
            <span
              data-testid="b2b-archived-count"
              title={archivedCount ? 'Klik op "Toon gearchiveerde" om te bekijken' : ''}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${archivedCount ? 'bg-muted text-muted-foreground border-border' : 'bg-muted/40 text-muted-foreground/60 border-border/40'}`}
            >
              Archief: {archivedCount}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/b2b/vat-audit"
            data-testid="vat-audit-link"
            title="VAT format audit"
            className="inline-flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
          >
            <ShieldAlert className="w-3.5 h-3.5" /> VAT-audit
          </Link>
          <Link
            to="/admin/b2b/new"
            data-testid="new-prescriber-btn"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Nieuwe voorschrijver
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[['all', 'Alle'], ['pending', 'In afwachting'], ['approved', 'Goedgekeurd']].map(([val, label]) => (
          <button
            key={val}
            data-testid={`b2b-filter-${val}`}
            onClick={() => setFilter(val)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${filter === val ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-card border border-border text-muted-foreground hover:border-primary/30'}`}
          >
            {label}
          </button>
        ))}
        <label className="ml-4 inline-flex items-center gap-2 text-xs text-muted-foreground select-none">
          <input
            data-testid="b2b-toggle-archived"
            type="checkbox"
            checked={includeArchived}
            onChange={e => setIncludeArchived(e.target.checked)}
          />
          Toon gearchiveerde
        </label>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={`b2b-skeleton-${i}`} className="h-14 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3.5 text-left font-semibold">Naam</th>
                <th className="px-5 py-3.5 text-left font-semibold">E-mail</th>
                <th className="px-5 py-3.5 text-left font-semibold">Bedrijf</th>
                <th className="px-5 py-3.5 text-left font-semibold">Beroep</th>
                <th className="px-5 py-3.5 text-center font-semibold">Koppeling</th>
                <th className="px-5 py-3.5 text-center font-semibold">Bron</th>
                <th className="px-5 py-3.5 text-center font-semibold">Status</th>
                <th className="px-5 py-3.5 text-right font-semibold">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(p => (
                <tr
                  key={p._id}
                  data-testid={`b2b-row-${p._id}`}
                  className={`transition-colors ${p.archived ? 'opacity-60' : 'hover:bg-muted/20'}`}
                >
                  <td className="px-5 py-3.5 font-medium text-foreground">
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/b2b/${p._id}`} className="hover:text-primary">
                        {[p.firstName, p.lastName].filter(Boolean).join(' ') || p.user?.name || '--'}
                      </Link>
                      {p.archived && (
                        <span data-testid={`archived-pill-${p._id}`} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-muted text-muted-foreground border border-border">ARCHIVED</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground">{p.email || p.user?.email || '--'}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{p.org?.companyName || '--'}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{PROFESSION_LABELS[p.profession] || p.profession || '--'}</td>
                  <td className="px-5 py-3.5 text-center">
                    {p.userId ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">LINKED</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">UNLINKED</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                      {p.source || 'admin'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    {p.isApproved ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Goedgekeurd</span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">In afwachting</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to={`/admin/b2b/${p._id}/edit`}
                        data-testid={`edit-b2b-${p._id}`}
                        title="Bewerken"
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Link>
                      <Link
                        to={`/admin/credits?personId=${p._id}`}
                        data-testid={`credits-b2b-${p._id}`}
                        title="Credits"
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Wallet className="w-3.5 h-3.5" />
                      </Link>
                      {p.archived ? (
                        <button
                          data-testid={`restore-b2b-${p._id}`}
                          onClick={() => restore(p)}
                          title="Herstellen"
                          className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                        >
                          <ArchiveRestore className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          data-testid={`archive-b2b-${p._id}`}
                          onClick={() => archive(p)}
                          title="Archiveren"
                          className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && <p className="text-center py-12 text-muted-foreground text-sm">Geen resultaten</p>}
        </div>
      )}
    </div>
  );
}
